/**
 * UnreadBadgeProvider — 全局未读红点的数据源（T4）。
 *
 * 挂载点：已登录路由的 __layout.tsx，整个已登录应用生命周期只挂一次。
 * 登录页不挂载，避免未登录请求通知接口触发 401 重定向循环。
 * 内部复用 useUnreadCount，统一提供总通知未读数和审批提醒未读数。
 *
 * 失败静默：unread-count 拉取失败时红点退化为 0，不弹错误——红点是增强
 * 信息，不能成为阻塞用户的告警源。
 */

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useUnreadCount } from '../hooks';
import { UnreadBadgeContext } from '../../../shared/ui/unreadBadge';
import { readAccess } from '../../../shared/api/tokens';

export interface UnreadBadgeProviderProps {
  children: React.ReactNode;
}

export const UnreadBadgeProvider: React.FC<UnreadBadgeProviderProps> = ({ children }) => {
  const { count, refresh: refreshAll } = useUnreadCount();
  const { count: approvalCount, refresh: refreshApprovals } = useUnreadCount('delete_request_filed');
  const refreshing = useRef<Promise<void> | null>(null);
  const lastRefreshAt = useRef(0);
  const refresh = useCallback(async () => {
    if (!readAccess()) return;
    const now = Date.now();
    if (now - lastRefreshAt.current < 15_000) return;
    if (refreshing.current) return refreshing.current;
    lastRefreshAt.current = now;
    const task = Promise.all([refreshAll(), refreshApprovals()]).then(() => undefined).finally(() => {
      refreshing.current = null;
    });
    refreshing.current = task;
    await task;
  }, [refreshAll, refreshApprovals]);

  useEffect(() => {
    const onFocus = () => { void refresh(); };
    const onOnline = () => { void refresh(); };
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
    };
  }, [refresh]);

  const value = useMemo(
    () => ({
      count,
      approvalCount,
      refresh,
    }),
    [approvalCount, count, refresh],
  );

  return <UnreadBadgeContext.Provider value={value}>{children}</UnreadBadgeContext.Provider>;
};

export default UnreadBadgeProvider;
